import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import MultiselectField from '../ApeUI/SelectField/MultiselectField';
import { Toggle } from '../ApeUI';
import {
  engineMultiselectItems, selectedEngineRows, enginesFromCloseEvent, sameEngineSet,
} from '../../utils/aiAssist';

import './ChannelsAiAssistTable.scss';

// Per-channel row inside the group-level GENERATE WITH AI table. Each control
// reads the channel's self-contained aiAssist object directly (no merge with the
// group — the group master toggle has already cascaded enabled into it) and
// writes the touched field back. The whole row is greyed when the channel is
// disabled; the toggle itself stays clickable while the group is on so the
// channel can be re-enabled (it's locked off when the group master is off).
const ChannelAiAssistRow = ({
  channel, groupEnabled, onSave,
}) => {
  const ai = channel.aiAssist || {};

  const channelEnabled = Boolean(ai.enabled);
  const engines = ai.enabledEngines || [];
  const cap = ai.perChannelMonthlyCap ?? 0;

  // Budget is a local draft so the operator can type freely; commit on blur.
  const [capDraft, setCapDraft] = useState(cap);
  useEffect(() => { setCapDraft(cap); }, [cap]);

  const onEnginesChange = list => {
    if (!list) {
      return;
    }
    const next = enginesFromCloseEvent(list, engines);
    // An enabled channel must keep >=1 engine — ignore an empty selection.
    // (To remove AI entirely, turn the channel off.)
    if (channelEnabled && next.length === 0) {
      return;
    }
    if (!sameEngineSet(next, engines)) {
      onSave({ enabledEngines: next });
    }
  };

  const onCapBlur = () => {
    const value = Number(capDraft);
    if (!Number.isFinite(value) || value < 0) {
      setCapDraft(cap);
      return;
    }
    if (value !== cap) {
      onSave({ perChannelMonthlyCap: value });
    }
  };

  return (
    <TableRow className={`ai-assist-channels-row ${channelEnabled ? '' : 'row-disabled'}`}>
      <TableCell>
        <div className="channel-name">{channel.name}</div>
        {channel.url && <div className="channel-url">{channel.url}</div>}
      </TableCell>
      <TableCell>
        <MultiselectField
          name="Supported Creatives"
          placeholder="Select creatives"
          readOnly={!channelEnabled}
          items={engineMultiselectItems(engines)}
          savedSelectedItems={selectedEngineRows(engines).map(({ label }) => label)}
          handleClose={onEnginesChange}
        />
      </TableCell>
      <TableCell>
        <input
          type="number"
          className="ai-assist-budget-input"
          min={0}
          step="any"
          value={capDraft}
          disabled={!channelEnabled}
          onChange={event => setCapDraft(event.target.value)}
          onBlur={onCapBlur}
        />
      </TableCell>
      <TableCell>
        <Toggle
          toggled={channelEnabled}
          disabled={!groupEnabled}
          onChange={value => onSave({ enabled: value })}
        />
      </TableCell>
    </TableRow>
  );
};

ChannelAiAssistRow.propTypes = {
  channel: PropTypes.shape({
    publisherId: PropTypes.string,
    name: PropTypes.string,
    url: PropTypes.string,
    aiAssist: PropTypes.shape({}),
  }).isRequired,
  groupEnabled: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
};

const ChannelsAiAssistTable = ({
  channels, groupEnabled, onChannelSave,
}) => {
  const activeChannels = (channels || []).filter(channel => channel.status !== 'inactive');

  if (!activeChannels.length) {
    return <p className="ai-assist-channels-empty">No channels in this group yet.</p>;
  }

  return (
    <Table className="ai-assist-channels-table">
      <TableHead>
        <TableRow>
          <TableCell>Channel</TableCell>
          <TableCell>Supported Creatives</TableCell>
          <TableCell>Budget (USD)</TableCell>
          <TableCell>Enabled</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {activeChannels.map(channel => (
          <ChannelAiAssistRow
            key={channel.publisherId || channel._id}
            channel={channel}
            groupEnabled={groupEnabled}
            onSave={patch => onChannelSave(channel, patch)}
          />
        ))}
      </TableBody>
    </Table>
  );
};

ChannelsAiAssistTable.propTypes = {
  channels: PropTypes.arrayOf(PropTypes.shape({})),
  groupEnabled: PropTypes.bool,
  onChannelSave: PropTypes.func.isRequired,
};

ChannelsAiAssistTable.defaultProps = {
  channels: [],
  groupEnabled: false,
};

export default ChannelsAiAssistTable;
